import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Card, Form, Spinner, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import DateRangePicker, { calcDatePreset } from '../../components/fields/DateRangePicker';
import PaginationComponent from '../../components/PaginationComponent';
import QtyDual, { formatQtyByPackage } from '../../utils/inventoryUnit';
import { inventoryAPI } from '../../services';

const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('vi-VN') : '—');
const LIMIT = 20;

// ─── #200902 (lệnh sếp update_id=731599164, 28/08/2026) ──────────────────────────────────────────
// Cột "Lệch lớn nhất" cũ vẽ `max_abs_variance_pct` — số ĐÃ BỎ DẤU, nên "25.71%" không cho biết ca
// đó THIẾU hàng hay DƯ hàng, phải bấm mở chi tiết mới thấy. Nay ô này chở 2 số, cả 2 GIỮ DẤU:
// dòng trên là NVL lệch mạnh nhất, dòng dưới là trung vị lệch của ca (BE tính, xem
// `shift-check-variance.util.ts`).
//
// QUY ƯỚC DẤU (thống nhất với toàn màn, chốt ở #172543/#192444):
//   ÂM  = đếm tay ÍT hơn sổ sách = THIẾU/hao hụt → TÔ ĐỎ.
//   DƯƠNG = đếm tay NHIỀU hơn sổ sách = DƯ      → KHÔNG tô đỏ.
const fmtSignedPct = (n) =>
  n == null ? '—' : `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n).toFixed(2)}%`;
const signClass = (n) => (n != null && n < 0 ? 'text-danger' : '');

function VarianceCell({ row }) {
  return (
    // `text-nowrap` để 2 số không bị bẻ dòng giữa chừng trên màn điện thoại (~390px) — bảng này đã
    // 10 cột và cuộn ngang, bẻ dòng số là hỏng cả cột.
    <div className="lh-sm text-nowrap">
      <div className={`fw-semibold ${signClass(row.signed_max_variance_pct)}`}>
        {fmtSignedPct(row.signed_max_variance_pct)}
      </div>
      <div className={`small ${row.median_variance_pct == null ? 'text-muted' : signClass(row.median_variance_pct)}`}>
        trung vị {fmtSignedPct(row.median_variance_pct)}
      </div>
    </div>
  );
}

function FlaggedMaterialsCard({ branchId, materials, onUnflag, unflagging }) {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(false);
  const materialsById = useMemo(() => new Map((materials || []).map((m) => [m.id, m])), [materials]);

  const fetchFlags = useCallback(async () => {
    if (!branchId) { setFlags([]); return; }
    try {
      setLoading(true);
      const rows = await inventoryAPI.getShiftCheckFlags(branchId);
      setFlags(rows || []);
    } catch (err) {
      toast.error(err?.message || 'Không thể tải danh sách NVL gắn cờ');
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => { fetchFlags(); }, [fetchFlags]);

  // Cho phép tab cha refresh khi có thay đổi từ nơi khác (ví dụ tab Hao hụt NVL)
  useEffect(() => {
    const handler = () => fetchFlags();
    window.addEventListener('focus', handler);
    return () => window.removeEventListener('focus', handler);
  }, [fetchFlags]);

  if (!branchId) {
    return <Alert variant="secondary" className="small">Vui lòng chọn chi nhánh để xem NVL gắn cờ.</Alert>;
  }

  return (
    <Card className="mb-3">
      <Card.Body>
        <Card.Title className="h6">NVL đang gắn cờ</Card.Title>
        {loading && <div className="text-center py-3"><Spinner size="sm" /></div>}
        {!loading && flags.length === 0 && (
          <p className="text-muted small mb-0">
            Chi nhánh này chưa gắn cờ NVL nào — gắn cờ ở tab "Hao hụt NVL".
          </p>
        )}
        {!loading && flags.length > 0 && (
          <Table bordered size="sm" responsive hover>
            <thead>
              <tr>
                <th>NVL</th>
                <th className="text-end">Tồn hiện tại</th>
                <th className="text-end">Ngưỡng riêng (%)</th>
                <th>Gắn bởi</th>
                <th>Lúc</th>
                <th className="text-center">Bỏ cờ</th>
              </tr>
            </thead>
            <tbody>
              {flags.map((f) => {
                const mat = materialsById.get(f.material_id) ?? { unit: f.unit };
                return (
                  <tr key={f.material_id}>
                    <td>{f.name}</td>
                    <td className="text-end"><QtyDual quantityInBase={f.theoretical_quantity} material={mat} /></td>
                    <td className="text-end">{f.threshold_pct != null ? `${f.threshold_pct}%` : <span className="text-muted">Mặc định</span>}</td>
                    <td>{f.flagged_by?.full_name || '—'}</td>
                    <td>{fmtDateTime(f.flagged_at)}</td>
                    <td className="text-center">
                      <Form.Check
                        type="switch"
                        id={`unflag-${f.material_id}`}
                        checked
                        disabled={!!unflagging[f.material_id]}
                        onChange={() => onUnflag(f.material_id, f.name, fetchFlags)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}

export default function ShiftMaterialCheckTab({ branchId, materials, currentUserId }) {
  const [range, setRange] = useState(() => calcDatePreset('thisMonth'));
  const [phase, setPhase] = useState('');
  const [onlyVariance, setOnlyVariance] = useState(false);
  // #013556 (lệnh sếp 24/08/2026): màn hậu kiểm — lọc theo nhân viên (tên, khớp với dữ liệu đã
  // tải) + chỉ hiện ca bị hệ thống tự gắn cờ "kiểm ẩu".
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [onlySuspicious, setOnlySuspicious] = useState(false);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [unflagging, setUnflagging] = useState({});

  const fetchHistory = useCallback(async () => {
    if (!range.from || !range.to) return;
    try {
      setLoading(true);
      setError(null);
      const params = { from_date: range.from, to_date: range.to, page, limit: LIMIT };
      if (branchId) params.branch_id = branchId;
      if (phase) params.phase = phase;
      if (onlyVariance) params.only_variance = 'true';
      if (onlySuspicious) params.only_suspicious = 'true';
      const res = await inventoryAPI.getShiftMaterialChecks(params);
      setRows(res?.data || []);
      setTotal(res?.total || 0);
    } catch (err) {
      const message = err?.message || 'Không thể tải lịch sử kiểm NVL theo ca';
      toast.error(message);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [branchId, range, phase, onlyVariance, onlySuspicious, page]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);
  useEffect(() => { setPage(1); }, [branchId, range, phase, onlyVariance, onlySuspicious]);

  const visibleRows = useMemo(() => {
    const q = employeeSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (r.employee?.full_name || '').toLowerCase().includes(q));
  }, [rows, employeeSearch]);

  const SUSPICIOUS_REASON_LABELS = {
    exact_match_all: 'Số nhập trùng khít tuyệt đối toàn bộ NVL',
    too_fast: 'Nhập quá nhanh so với số dòng NVL',
  };

  const handleUnflag = useCallback(async (materialId, materialName, refetchFlags) => {
    if (!branchId) return;
    if (!window.confirm(`Bỏ cờ kiểm ca cho "${materialName}"?`)) return;
    setUnflagging((prev) => ({ ...prev, [materialId]: true }));
    try {
      await inventoryAPI.setShiftCheckFlag(branchId, materialId, {
        is_required: false,
        current_user_id: currentUserId,
      });
      toast.success(`Đã bỏ cờ kiểm ca cho ${materialName}`);
      await refetchFlags();
    } catch (err) {
      toast.error(err?.message || 'Không thể bỏ cờ kiểm ca');
    } finally {
      setUnflagging((prev) => {
        const next = { ...prev };
        delete next[materialId];
        return next;
      });
    }
  }, [branchId, currentUserId]);

  return (
    <div>
      <FlaggedMaterialsCard
        branchId={branchId}
        materials={materials}
        onUnflag={handleUnflag}
        unflagging={unflagging}
      />

      <Card>
        <Card.Body>
          <Card.Title className="h6">Lịch sử kiểm theo ca</Card.Title>

          <div className="d-flex flex-wrap gap-3 align-items-end mb-3">
            <div style={{ minWidth: 320 }}>
              <DateRangePicker
                fromDate={range.from}
                toDate={range.to}
                onChange={(from, to) => setRange({ from, to })}
                defaultPreset="thisMonth"
              />
            </div>
            <Form.Group controlId="material-check-phase">
              <Form.Label className="small mb-1">Đầu/Cuối ca</Form.Label>
              <Form.Select size="sm" value={phase} onChange={(e) => setPhase(e.target.value)}>
                <option value="">Tất cả</option>
                <option value="OPENING">Đầu ca</option>
                <option value="CLOSING">Cuối ca</option>
              </Form.Select>
            </Form.Group>
            <Form.Check
              type="checkbox"
              id="only-variance"
              label="Chỉ hiện ca có lệch"
              checked={onlyVariance}
              onChange={(e) => setOnlyVariance(e.target.checked)}
            />
            <Form.Check
              type="checkbox"
              id="only-suspicious"
              label="Chỉ hiện ca bị gắn cờ kiểm ẩu"
              checked={onlySuspicious}
              onChange={(e) => setOnlySuspicious(e.target.checked)}
            />
            <Form.Group controlId="material-check-employee-search">
              <Form.Label className="small mb-1">Lọc theo nhân viên</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Tên nhân viên..."
                value={employeeSearch}
                onChange={(e) => setEmployeeSearch(e.target.value)}
                style={{ minWidth: 180 }}
              />
            </Form.Group>
          </div>

          {loading && <div className="text-center py-4"><Spinner /></div>}

          {!loading && error && <Alert variant="danger">{error}</Alert>}

          {!loading && !error && visibleRows.length === 0 && (
            <p className="text-muted text-center py-3 mb-0">Không có dữ liệu kiểm NVL khớp bộ lọc hiện tại.</p>
          )}

          {!loading && !error && visibleRows.length > 0 && (
            <>
              <Table bordered size="sm" responsive hover>
                <thead>
                  <tr>
                    <th>Ngày/giờ</th>
                    <th>Chi nhánh</th>
                    <th>Ca</th>
                    <th>NV</th>
                    <th>Đầu/Cuối ca</th>
                    <th className="text-end">Số NVL</th>
                    <th className="text-end">Số NVL lệch</th>
                    <th className="text-end">
                      Lệch lớn nhất
                      <div className="fw-normal text-muted" style={{ fontSize: '0.75em' }}>&amp; trung vị</div>
                    </th>
                    <th>Trạng thái</th>
                    <th>Kiểm ẩu</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((r) => (
                    <React.Fragment key={r.id}>
                      <tr
                        role="button"
                        onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                        className={r.is_suspicious ? 'table-danger' : r.items_with_variance > 0 ? 'table-warning' : ''}
                      >
                        <td>{fmtDateTime(r.created_at)}</td>
                        <td>{r.branch?.name || '—'}</td>
                        <td>{r.shift ? `${fmtDateTime(r.shift.start_time)} - ${fmtDateTime(r.shift.end_time)}` : '—'}</td>
                        <td>{r.employee?.full_name || '—'}</td>
                        <td>
                          <Badge bg={r.phase === 'OPENING' ? 'info' : 'secondary'}>
                            {r.phase === 'OPENING' ? 'Đầu ca' : 'Cuối ca'}
                          </Badge>
                        </td>
                        <td className="text-end">{r.total_items}</td>
                        <td className="text-end">{r.items_with_variance}</td>
                        <td className="text-end"><VarianceCell row={r} /></td>
                        <td>
                          {r.is_reconciled ? (
                            <Badge bg="success">Khớp</Badge>
                          ) : (
                            <Badge bg="warning" text="dark">Đang chờ đối soát</Badge>
                          )}
                        </td>
                        <td>
                          {r.is_suspicious ? (
                            <Badge bg="danger">⚠ Kiểm ẩu</Badge>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                      </tr>
                      {expandedId === r.id && (
                        <tr>
                          <td colSpan={10} className="bg-light">
                            {Array.isArray(r.suspicious_reasons) && r.suspicious_reasons.length > 0 && (
                              <p className="small mb-2 text-danger">
                                <strong>Dấu hiệu kiểm ẩu:</strong>{' '}
                                {r.suspicious_reasons
                                  .map((reason) => SUSPICIOUS_REASON_LABELS[reason] || reason)
                                  .join('; ')}
                              </p>
                            )}
                            {r.explanation && (
                              <p className="small mb-2"><strong>Giải trình:</strong> {r.explanation}</p>
                            )}
                            <Table size="sm" borderless className="mb-0">
                              <thead>
                                <tr>
                                  <th>NVL</th>
                                  <th className="text-end">Dự kiến</th>
                                  <th className="text-end">Thực đếm</th>
                                  <th className="text-end">Lệch</th>
                                  <th className="text-end">Lệch %</th>
                                  <th>Ghi chú</th>
                                </tr>
                              </thead>
                              <tbody>
                                {r.items.map((it) => (
                                  <tr key={it.material_id}>
                                    <td>
                                      {it.name}
                                      {it.opening_missing && (
                                        <Badge bg="light" text="dark" className="ms-2">Mới gắn cờ</Badge>
                                      )}
                                    </td>
                                    <td className="text-end"><QtyDual quantityInBase={it.expected_quantity} material={{ unit: it.unit }} /></td>
                                    {/* #144941: "Thực đếm" là số NV bấm tay -> hiển thị theo breakdown còn nguyên/dở dang đã lưu, không quy đổi liên tục */}
                                    <td className="text-end">{formatQtyByPackage(it, it.packages)}</td>
                                    <td className={`text-end ${it.variance > 0 ? 'text-success' : it.variance < 0 ? 'text-danger' : ''}`}>
                                      {it.variance > 0 ? '+' : ''}<QtyDual quantityInBase={it.variance} material={{ unit: it.unit }} />
                                    </td>
                                    <td className="text-end">{it.variance_pct.toFixed(2)}%</td>
                                    <td className="small text-muted">{it.note || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </Table>
              <PaginationComponent
                totalItems={total}
                itemsPerPage={LIMIT}
                currentPage={page}
                onPageChange={setPage}
                loading={loading}
              />
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
